import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  theme,
  Modal,
  Layout,
  Card,
  Typography,
  Button
} from 'antd';
import { LayoutOutlined, WhatsAppOutlined, LeftOutlined, BgColorsOutlined, EyeOutlined } from '@ant-design/icons';
import { useAssistant } from '../../../../contexts/AssistantContext';
import { useAccount } from '../../../../store';
import config from '../../../../config';
import { useAuthGuard } from '../../../../hooks/useAuthGuard';
import { WIDGET_DATA } from '../../constants';
import { WidgetDataItem } from '../../types';
import { AppBreadcrumb, useBreadcrumb } from '../../../breadcrumb';
import MyAssistantWhatsAppConfig from './MyAssistantWhatsAppConfig/MyAssistantWhatsAppConfig';
import MyAssistantWebConfig from './MyAssistantWebConfig/MyAssistantWebConfig';
import MyAssistantChannelCard from './MyAssistantChannelCard/MyAssistantChannelCard';
import MyAssistantWebsiteWidgetConfig from './MyAssistantWebsiteWidget/MyAssistantWebsiteWidgetConfig';
import { getIntegrationCode, handleCopy } from '../../utils';
import { Appearance } from '../Appearance/MyAssistantAppearance';

const { useToken } = theme;
const { Link } = Typography;

// Componente para el modal de WhatsApp
const WhatsAppModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  chatbotId: string;
  chatbotData: any;
}> = ({ isOpen, onClose, chatbotId, chatbotData }) => (
  <Modal
    open={isOpen}
    onCancel={onClose}
    footer={null}
    destroyOnHidden
    width={'80%'}
  >
    <MyAssistantWhatsAppConfig
      chatbotId={chatbotId}
      chatbotData={chatbotData}
    />
  </Modal>
);

// Componente para el modal de Web
const WebModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => (
  <Modal
    open={isOpen}
    onCancel={onClose}
    footer={null}
    destroyOnHidden
    width={'80%'}
  >
    <MyAssistantWebConfig />
  </Modal>
);

// Componente para la sección de Website Widget
const WebsiteWidgetSection: React.FC<{
  widgetData: WidgetDataItem & { icon: React.ReactNode; onClick: () => void };
  integrationCode: string;
  onAppearanceClick: () => void;
  assistantId: string;
}> = ({ widgetData, integrationCode, onAppearanceClick, assistantId }) => {
  const handlePreview = () => {
    const previewUrl = `${window.location.origin}/chat/${assistantId}?preview=true`;
    window.open(previewUrl, '_blank');
  };

  return (
    <Card>
      <MyAssistantChannelCard
        widgetData={widgetData}
        headerAction={
          <Flex gap={8}>
            <Button
              icon={<EyeOutlined />}
              onClick={handlePreview}
              type="default"
            >
              Preview
            </Button>
            <Button
              icon={<BgColorsOutlined />}
              onClick={onAppearanceClick}
              type="default"
            >
             Appearance
            </Button>
          </Flex>
        }
      >
        <MyAssistantWebsiteWidgetConfig
          integrationCode={integrationCode}
          onCopy={() => handleCopy(integrationCode)}
        />
      </MyAssistantChannelCard>
    </Card>
  );
};

// Componente para la sección de WhatsApp
const WhatsAppSection: React.FC<{
  widgetData: WidgetDataItem & { icon: React.ReactNode; onClick: () => void };
}> = ({ widgetData }) => (
  <Card>
    <MyAssistantChannelCard widgetData={widgetData} />
  </Card>
);

// Componente principal
export default function MyAssistantChannels(): React.ReactElement {
  useAuthGuard(); // Proteger esta página con autenticación
  
  const navigate = useNavigate();
  const { currentAccount } = useAccount();
  const { currentAssistant } = useAssistant();
  const chatbotId = currentAssistant?.id ?? '';

  const { token } = useToken();
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [showWebModal, setShowWebModal] = useState(false);
  const [showAppearanceView, setShowAppearanceView] = useState(false);

  const integrationCode = getIntegrationCode(config.app.domain, currentAssistant?.id);

  // Función para obtener el componente de icono
  const getIconComponent = (widget: WidgetDataItem) => {
    const IconComponent = widget.iconType === 'layout' ? LayoutOutlined : WhatsAppOutlined;
    return <IconComponent style={widget.iconStyle} />;
  };

  // Preparar los datos del widget con sus manejadores
  const widgetDataWithHandlers = WIDGET_DATA.map((widget) => ({
    ...widget,
    icon: getIconComponent(widget),
    onClick: widget.iconType === 'layout'
      ? () => setShowWebModal(true)
      : () => setShowWhatsappModal(true),
  }));

  const breadcrumbItems = useBreadcrumb({
    section: 'Channels',
    includeAssistant: true,
    ...(showAppearanceView && {
      additionalItems: [{
        label: 'Appearance',
        onClick: () => setShowAppearanceView(false),
        isActive: true
      }]
    }),
    onSectionClick: showAppearanceView ? () => setShowAppearanceView(false) : undefined
  });

  // Si está en vista de Appearance, mostrar solo ese componente
  if (showAppearanceView) {
    return (
      <>
        <AppBreadcrumb items={breadcrumbItems} />
        <Appearance />
      </>
    );
  }

  // Vista normal de Channels
  return (
    <>
      <AppBreadcrumb items={breadcrumbItems} />
      <Card>
        <Flex
          vertical
          gap={token.marginMD}
          style={{
            margin: '0 auto',
            width: '100%',
          }}
        >
        <WebsiteWidgetSection
          widgetData={widgetDataWithHandlers[0]}
          integrationCode={integrationCode}
          onAppearanceClick={() => setShowAppearanceView(true)}
          assistantId={chatbotId}
        />

        <WhatsAppSection widgetData={widgetDataWithHandlers[1]} />

        <WhatsAppModal
          isOpen={showWhatsappModal}
          onClose={() => setShowWhatsappModal(false)}
          chatbotId={chatbotId}
          chatbotData={currentAssistant}
        />

        <WebModal
          isOpen={showWebModal}
          onClose={() => setShowWebModal(false)}
        />
      </Flex>
    </Card>
    </>
  );
}
